import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, Banknote, Smartphone, Clock, Zap, Shield, Truck, Tag } from "lucide-react";
import { PortalCumulativeStats } from "./PortalCumulativeStats";

const PAY_ICONS = {
  card:   <CreditCard className="w-3.5 h-3.5" />,
  viva:   <CreditCard className="w-3.5 h-3.5" />,
  online: <CreditCard className="w-3.5 h-3.5" />,
  cash:   <Banknote className="w-3.5 h-3.5" />,
  mbway:  <Smartphone className="w-3.5 h-3.5" />,
};

const PAY_LABELS = {
  card: "Tarjeta", viva: "Viva Wallet", online: "Online",
  cash: "Efectivo", mbway: "MB Way",
};

const TYPE_EMOJI = { delivery: "🛵", takeaway: "🥡", table: "🍽️", dine_in: "🍽️" };

const CARD_ACCENT = {
  delivery: "border-l-4 border-l-indigo-300",
  takeaway: "border-l-4 border-l-amber-300",
  table:    "border-l-4 border-l-emerald-300",
  dine_in:  "border-l-4 border-l-emerald-300",
};

function deliveryMins(order) {
  if (!order.created_at || !order.delivered_at) return null;
  const mins = Math.round((new Date(order.delivered_at) - new Date(order.created_at)) / 60000);
  return mins > 0 && mins < 180 ? mins : null;
}

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
}

export function PortalFinancialTab({ customer, orders }) {
  const cashback     = customer?.cashback_balance || 0;
  const totalFees    = orders.reduce((s, o) => s + (o.delivery_fee || o.shipping_fee || 0), 0);
  const totalDisc    = orders.reduce((s, o) => s + (o.discount_amount || o.coupon_discount || 0), 0);
  const totalEarned  = orders.reduce((s, o) => s + (o.cashback_earned || 0), 0);

  const quickStats = [
    totalFees   > 0 && { label: "Gastos de envío",  value: `€${totalFees.toFixed(2)}`,    color: "text-gray-600",    icon: <Truck className="w-3.5 h-3.5" /> },
    totalDisc   > 0 && { label: "Descuentos",       value: `-€${totalDisc.toFixed(2)}`,   color: "text-emerald-600", icon: <Tag className="w-3.5 h-3.5" /> },
    totalEarned > 0 && { label: "Cashback ganado",  value: `+€${totalEarned.toFixed(2)}`, color: "text-amber-600",   icon: <Zap className="w-3.5 h-3.5" /> },
  ].filter(Boolean);

  return (
    <div className="space-y-4">

      {/* Section label */}
      <div className="flex items-center gap-1.5 px-0.5">
        <Shield className="w-3.5 h-3.5 text-gray-400" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Área financiera</p>
      </div>

      {/* Cashback balance — hero */}
      {cashback > 0 && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-md">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-0.5">
                Saldo disponible
              </p>
              <p className="text-3xl font-black text-amber-700">€{cashback.toFixed(2)}</p>
              <p className="text-xs text-amber-500 mt-0.5">Disponible en tu próximo pedido</p>
            </div>
            <Zap className="w-10 h-10 text-amber-300 shrink-0" />
          </CardContent>
        </Card>
      )}

      {/* Quick aggregate stats */}
      {quickStats.length > 0 && (
        <div className={`grid gap-2 grid-cols-${quickStats.length}`}>
          {quickStats.map(s => (
            <div key={s.label} className="text-center p-3 bg-white rounded-xl border shadow-sm">
              <div className={`flex items-center justify-center gap-1 mb-0.5 ${s.color}`}>
                {s.icon}
                <p className="text-base font-black">{s.value}</p>
              </div>
              <p className="text-[11px] text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Full cumulative stats */}
      <PortalCumulativeStats customer={customer} orders={orders} />

      {/* Per-order financial breakdown */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2 px-0.5">
          Historial financiero
        </p>
        <div className="space-y-2">
          {orders.map(o => {
            const savings   = (o.discount_amount || 0) + (o.coupon_discount || 0);
            const earned    = o.cashback_earned || 0;
            const used      = o.cashback_used   || 0;
            const fee       = o.delivery_fee || o.shipping_fee || 0;
            const mins      = deliveryMins(o);
            const payKey    = o.payment_method;
            const payLabel  = payKey ? (PAY_LABELS[payKey] || payKey) : null;
            const payIcon   = payKey ? PAY_ICONS[payKey] : null;
            const accent    = CARD_ACCENT[o.order_type] || "border-l-4 border-l-gray-200";

            const hasDetails = payLabel || savings > 0 || earned > 0 || used > 0 || fee > 0 || mins;
            if (!hasDetails) return null;

            return (
              <Card key={o.id} className={`shadow-sm ring-1 ring-gray-100 border-0 ${accent}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">
                      {TYPE_EMOJI[o.order_type] || ""} {fmtDate(o.created_at)}
                    </span>
                    <span className="font-black text-gray-900 text-sm">€{(o.total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {payIcon && payLabel && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        {payIcon}{payLabel}
                      </span>
                    )}
                    {fee > 0 && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Truck className="w-3 h-3" />€{fee.toFixed(2)}
                      </span>
                    )}
                    {mins && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{mins} min
                      </span>
                    )}
                    {savings > 0 && (
                      <span className="text-xs text-emerald-600 font-semibold flex items-center gap-0.5">
                        <Tag className="w-3 h-3" />-€{savings.toFixed(2)}
                      </span>
                    )}
                    {earned > 0 && (
                      <span className="text-xs text-amber-600 font-semibold flex items-center gap-0.5">
                        <Zap className="w-3 h-3" />+€{earned.toFixed(2)}
                      </span>
                    )}
                    {used > 0 && (
                      <span className="text-xs text-purple-600 font-semibold">€{used.toFixed(2)} usado</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
